import {
  SignalStageSchema,
  SignalStatus,
  defaultStageForSignalStatus,
  type SignalStageType,
  type SignalStatusType,
} from '@terra/shared-schemas-orchestrator';
import { resolveStageForStatusUpdate } from './signalStatusRelatedFinding';

export const FINDING_DELETE_SIGNAL_ACTION = {
  KEEP: 'KEEP',
  CHANGE_STATUS: 'CHANGE_STATUS',
  DELETE: 'DELETE',
} as const;

export type FindingDeleteSignalAction =
  (typeof FINDING_DELETE_SIGNAL_ACTION)[keyof typeof FINDING_DELETE_SIGNAL_ACTION];

export type FindingDeleteRelatedSignal = {
  id: string;
  title: string | null;
  stage: SignalStageType | null;
};

export const FINDING_DELETE_SIGNAL_STATUS_OPTIONS: readonly SignalStatusType[] =
  [
    SignalStatus.DISPROVEN_VULNERABILITY,
    SignalStatus.POTENTIAL_VULNERABILITY,
    SignalStatus.CONFIRMED_VULNERABILITY,
    SignalStatus.BLOCKED,
    SignalStatus.DUPLICATE,
  ];

export type FindingDeleteSignalDisposition =
  | { action: typeof FINDING_DELETE_SIGNAL_ACTION.KEEP }
  | {
      action: typeof FINDING_DELETE_SIGNAL_ACTION.CHANGE_STATUS;
      status: SignalStatusType;
    }
  | { action: typeof FINDING_DELETE_SIGNAL_ACTION.DELETE };

export const isFindingDeleteConfirmDisabled = ({
  hasRelatedSignal,
  action,
  selectedStatus,
}: {
  hasRelatedSignal: boolean;
  action: FindingDeleteSignalAction;
  selectedStatus: SignalStatusType | null;
}): boolean => {
  if (!hasRelatedSignal) {
    return false;
  }

  return (
    action === FINDING_DELETE_SIGNAL_ACTION.CHANGE_STATUS && !selectedStatus
  );
};

export const buildFindingDeleteSignalDisposition = ({
  action,
  selectedStatus,
}: {
  action: FindingDeleteSignalAction;
  selectedStatus: SignalStatusType | null;
}): FindingDeleteSignalDisposition | null => {
  if (action === FINDING_DELETE_SIGNAL_ACTION.CHANGE_STATUS) {
    if (!selectedStatus) {
      return null;
    }

    return {
      action: FINDING_DELETE_SIGNAL_ACTION.CHANGE_STATUS,
      status: selectedStatus,
    };
  }

  if (action === FINDING_DELETE_SIGNAL_ACTION.DELETE) {
    return { action: FINDING_DELETE_SIGNAL_ACTION.DELETE };
  }

  return { action: FINDING_DELETE_SIGNAL_ACTION.KEEP };
};

export const toFindingDeleteRelatedSignal = ({
  signalSummary,
  signalId,
}: {
  signalSummary?: {
    id: string;
    title: string | null;
    stage: string | null;
  } | null;
  signalId?: string | null;
}): FindingDeleteRelatedSignal | null => {
  if (signalSummary) {
    const parsedStage = SignalStageSchema.safeParse(signalSummary.stage);

    return {
      id: signalSummary.id,
      title: signalSummary.title,
      stage: parsedStage.success ? parsedStage.data : null,
    };
  }

  if (signalId) {
    return { id: signalId, title: null, stage: null };
  }

  return null;
};

export const resolveStageForFindingDeleteStatusUpdate = ({
  currentStage,
  newStatus,
}: {
  currentStage: SignalStageType | null;
  newStatus: SignalStatusType;
}): SignalStageType => {
  if (!currentStage) {
    return defaultStageForSignalStatus({ status: newStatus });
  }

  return resolveStageForStatusUpdate({
    currentStage,
    newStatus,
  });
};

export type BulkDeleteSignalsResult = {
  success: boolean;
  deletedIds: readonly string[];
};

export const didBulkDeleteIncludeSignalIds = ({
  result,
  expectedIds,
}: {
  result: BulkDeleteSignalsResult;
  expectedIds: readonly string[];
}): boolean => {
  if (!result.success) {
    return false;
  }

  return expectedIds.every((id) => result.deletedIds.includes(id));
};
